// complete - import statements
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadUser, setGuestMode } from './slices/authSlice';
import TopicCreate from './components/Topics/TopicCreate';
import Navbar from './components/Navbar';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Topics from './components/Topics/Topics';
import TopicDetail from './components/Topics/TopicDetail';
import ProtectedRoute from './ProtectedRoute';
import axios from 'axios';
import './App.css';
import MyPage from './components/MyPage/MyPage';
import AdminDashboard from './components/Admin/AdminDashboard';
import PointHistory from './components/MyPage/PointHistory';
import Footer from './components/Footer';

if (typeof window !== 'undefined' && !window.webkit) {
  window.webkit = {
    messageHandlers: {}
  };
}

// App 함수 내부의 Routes 설정
function App() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isAwake, setIsAwake] = useState(false);

  // 🚩 [추가] 점검 모드 상태
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  // 🚩 [추가] 앱 시작 시 내 최신 정보(알림 여부 등) 서버에서 가져오기
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(loadUser());
    } else {
      dispatch(setGuestMode());
    }
  }, [dispatch]);

  // 🚩 [추가] 서버 깨우기 로직
  useEffect(() => {
    const checkServer = async () => {
      try {
        // 백엔드의 기본 주소(/)로 요청을 보냅니다.
        // index.js에서 설정한 baseURL 덕분에 '/'만 입력해도 됩니다.
        await axios.get('/');
        setIsAwake(true);

        // 🚩 [추가] 점검 중인지 확인
        const statusRes = await axios.get('https://the-pivot-logical-battleground.onrender.com/api/auth/system-status');
        setIsUnderMaintenance(statusRes.data.isUnderMaintenance);

      } catch (err) {
        console.log("서버가 깨어나는 중...");
        // 실패 시 3초 후 다시 찌릅니다. (서버가 일어날 때까지 반복)
        setTimeout(checkServer, 3000);
      }
    };
    checkServer();
  }, []);

  // 서버가 준비되지 않았다면 로딩 화면을 보여줍니다.
  if (!isAwake) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className="loading-content">
          <div className="battle-spinner"></div>
          <h1>전장을 구축하고 있습니다...</h1>
          <p>서버가 깨어나는 중입니다. 잠시만 기다려주세요 (최대 1분)</p>
          <span className="loading-status">백엔드 응답 대기 중...</span>
        </div>
      </div>
    );
  }

  // 🚩 [추가] 2. 점검 중 화면 (단, ADMIN은 통과!)
  if (isUnderMaintenance && user?.role !== 'ADMIN') {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100vh', backgroundColor: '#0a0a0a', color: '#fff', textAlign: 'center', padding: '20px'
        }}>
            <h1 style={{fontSize: '3rem', color: '#ff4e50', marginBottom: '20px'}}>⚠️ SYSTEM MAINTENANCE</h1>
            <h2 style={{marginBottom: '30px'}}>현재 긴급 시스템 점검 중입니다.</h2>
            <p style={{fontSize: '1.2rem', color: '#aaa', maxWidth: '600px', lineHeight: '1.6'}}>
                보다 안정적인 전장 환경을 위해 서버를 정비하고 있습니다.<br/>
                잠시 후 다시 접속해 주십시오.
            </p>
            <div className="battle-spinner" style={{marginTop: '50px'}}></div>
            <p style={{marginTop: '20px', color: '#666', fontSize: '0.9rem'}}>THE PIVOT TEAM</p>
        </div>
    );
  }

  return (
    <Router>
      {/* 🚩 [추가] 관리자가 점검 중에 접속했을 때 알림 배너 */}
      {isUnderMaintenance && user?.role === 'ADMIN' && (
          <div style={{
              backgroundColor: '#ff4e50', color: 'white', textAlign: 'center', 
              padding: '10px', fontWeight: 'bold', position: 'fixed', top: 0, width: '100%', zIndex: 99999
          }}>
              🚨 현재 점검 모드가 켜져 있습니다. (일반 유저 접속 불가 / 관리자만 접속 중)
          </div>
      )}

      <Navbar />

      {/* 🚩 [핵심 수정] Routes를 감싸는 div에 강력한 minHeight를 부여합니다.
          이렇게 하면 데이터가 로딩 전이라도 푸터가 바닥(100vh 아래)에 고정됩니다. */}
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}> {/* 이 div가 남는 공간을 모두 차지해 푸터를 밀어냅니다. */}
          <Routes>
            <Route path="/" element={<Navigate to="/topics" replace />} />
            <Route
              path="/login"
              element={ user ? <Navigate to="/topics" replace /> : <Login /> }
            />
            <Route
              path="/register"
              element={ user ? <Navigate to="/topics" replace /> : <Register /> }
            />

            <Route path="/topics" element={
              <ProtectedRoute>
                <Topics />
              </ProtectedRoute>
            } />

            {/* 관리자 전용 새 전장 생성 경로 추가 */}
            <Route path="/topics/new" element={
              <ProtectedRoute adminOnly={true}>
                <TopicCreate />
              </ProtectedRoute>
            } />

            <Route path="/topics/:id" element={
              <ProtectedRoute>
                <TopicDetail />
              </ProtectedRoute>
            } />

            {/* 마이페이지 경로 추가 */}
            <Route path="/mypage" element={
              <ProtectedRoute>
                <MyPage />
              </ProtectedRoute>
            } />

            {/* 🚩 [추가] 포인트 내역 페이지 라우트 */}
            <Route path="/mypage/history" element={
              <ProtectedRoute>
                <PointHistory />
              </ProtectedRoute>
            } />

            {/* 관리자 대시보드 경로 추가 */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      

        {/* 🚩 [추가] 모든 페이지 하단에 Footer 표시 */}
        <Footer />
      </div>
    </Router>
  );
}

export default App;